import api from "@/services/api";
import {
  TimeUnit,
  ResourcesDatafilter,
  TagsDataFilter,
  TemporalDataFilter,
  SeparateResourcesFilter,
  DateRangeSelection,
  SelectionTemporalDataFilter,
  TemporalPeriodsType,
} from "@/services/data_filters";
import { ResourceChannel } from "@/services/resources";
import { upperFirst } from "lodash-es";
import {
  computed,
  ComputedRef,
  MaybeRef,
  reactive,
  ref,
  Ref,
  unref,
} from "vue";
import {
  type InfiniteData,
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/vue-query";
import {
  useCalculatedResources,
  useDetachedTemporalFilter,
  useDataFilters,
  useDetachedSelectionTemporalFilter,
  useDetachedTagFilter,
  useMetricFilter,
  SocialMediaMetricView,
  PostFormatView,
  CollabView,
  PostPromotionView,
} from "@/store/data_filters";
import { QueryFunctionContext } from "@/utils/typing";
import {
  PublicDashboardWidgetFilters,
  usePublicDashboardWidgetQueryFilters,
} from "@/services/public_dashboard";
import {
  ALL_STORY_METRICS,
  MetricFilterSet,
  MetricKey,
  SUPPORTED_STORY_METRICS_BY_CHANNEL,
} from "@/services/metrics";
import {
  CompetitorPost,
  useCompetitorsQuery,
  competitorById,
  Competitor,
} from "@/services/competitor_monitoring";
import { useResourcesQuery } from "@/services/resources";
import { isCompetitorPost } from "@/views/CompetitorMonitor";
import {
  FacebookOrganicResource,
  InstagramOrganicResource,
  Resource,
} from "@/services/resources";
import { filterCriteriaFormatter } from "@/utils/filterCriteriaFormatter";
import { HeatmapWeekdayData } from "@/utils/heatmap";
import { DateTime } from "luxon";
import {
  CalculatedMetricKey,
  isCalculatedMetric,
  MetricType,
  useCalculatedMetricKeys,
} from "@/services/calculated_metrics";
import { AscendingOrDescending } from "@/components/DataPresentation";

export type {
  SocialMediaMetricView,
  PostFormatView,
  CollabView,
  PostPromotionView,
};

export const SOCIAL_MEDIA_RESOURCE_CHANNELS = [
  ResourceChannel.LINKED_IN_ORGANIC,
  ResourceChannel.FACEBOOK_ORGANIC,
  ResourceChannel.INSTAGRAM_ORGANIC,
  ResourceChannel.YOUTUBE,
  ResourceChannel.TIK_TOK_ORGANIC,
  ResourceChannel.GOOGLE_SHEETS,
] as const satisfies ResourceChannel[];

export type SocialMediaResourceChannel =
  (typeof SOCIAL_MEDIA_RESOURCE_CHANNELS)[number];

export const socialMediaPostMetrics = [
  "impressions",
  "reach",
  "clicks",
  "linkClicks",
  "comments",
  "clickthroughRate",
  "shares",
  "saves",
  "engagement",
  "engagementRate",
  "reactions",
  "videoWatchTime",
  "pageViewsPerSession",
  "bounceRate",
  "conversions",
] as const satisfies MetricKey[];

export const socialMediaPostComparisonMetrics = [
  ...socialMediaPostMetrics,
  "postsPublished",
] as const satisfies MetricKey[];

// Metrics shown on a story card (post_type === "story"). Stories have a
// distinct metric set from feed posts (reach/views + navigation + replies),
// so story cards render THIS fixed set rather than the global post-metric
// selection (which contains clicks/CTR/saves that stories don't have). Kept
// out of `socialMediaPostMetrics` so they never appear in the picker for
// regular posts. Sourced from metrics.ts's ALL_STORY_METRICS (the cross-channel
// union) so it and SUPPORTED_STORY_METRICS_BY_CHANNEL stay one definition.
export const socialMediaStoryMetrics = ALL_STORY_METRICS;

// SUPPORTED_STORY_METRICS_BY_CHANNEL lives in metrics.ts (single source of truth,
// shared with METRIC_CHANNEL_AVAILABILITY). A channel not listed shows the full
// set (fail-open, matching the post-metric helper).
export function isStoryMetricSupportedForChannel(
  metric: MetricKey,
  channel: ResourceChannel,
): boolean {
  const supported = SUPPORTED_STORY_METRICS_BY_CHANNEL[channel];
  return supported ? supported.includes(metric) : true;
}

// The story-only subset (keys NOT already in socialMediaPostMetrics —
// reach/impressions/linkClicks/reactions/shares overlap and are excluded here).
export const socialMediaStoryOnlyMetrics = [
  "storyReplies",
  "storyInteractions",
  "storyProfileVisits",
  "storyFollows",
  "storyTapsForward",
  "storyTapsBack",
  "storyExits",
  "storySwipeForward",
] as const satisfies MetricKey[];

// Channels that can have stories — derived from SUPPORTED_STORY_METRICS_BY_CHANNEL
// so "does this resource selection involve stories" and "which story metrics does
// this channel supply" can't drift. Consumed by the widget story toggle and the
// story sort options.
export const STORY_CAPABLE_CHANNELS: readonly ResourceChannel[] = Object.keys(
  SUPPORTED_STORY_METRICS_BY_CHANNEL,
) as ResourceChannel[];

// Metrics offered in the post-level custom-dashboard widget picker (stat card /
// data table): regular post metrics PLUS the story-only metrics. Story metrics
// are exposed HERE (widgets) but deliberately NOT in socialMediaPostMetrics, so
// they don't leak into the Posts-tab regular-post column picker. The picker
// scopes them to IG/FB via METRIC_CHANNEL_AVAILABILITY.
export const socialMediaPostWidgetMetrics = [
  ...socialMediaPostComparisonMetrics,
  ...socialMediaStoryOnlyMetrics,
] as const satisfies MetricKey[];

// Per-channel metric support, expressed as an allowlist (which metrics each
// channel actually supplies) so a channel not yet wired up fails safe — it
// shows nothing rather than fabricated empty/zero tiles. Mirrors the backend
// METRIC_CHANNEL_AVAILABILITY polarity. A metric that renders for a channel it
// can't supply would read as a real zero rather than "not available here".
//
// Metrics every social channel provides (page/engagement + GA4-joined fields).
const BASE_SUPPORTED_POST_METRICS: readonly MetricKey[] = [
  "impressions",
  "comments",
  "reactions",
  "shares",
  "engagement",
  "engagementRate",
  "pageViewsPerSession",
  "bounceRate",
  "conversions",
];

const SUPPORTED_POST_METRICS_BY_CHANNEL: Partial<
  Record<ResourceChannel, readonly MetricKey[]>
> = {
  // Instagram organic: no click data; adds reach + saves.
  [ResourceChannel.INSTAGRAM_ORGANIC]: [
    ...BASE_SUPPORTED_POST_METRICS,
    "reach",
    "saves",
  ],
  // TikTok organic: no click data; adds reach + watch time.
  [ResourceChannel.TIK_TOK_ORGANIC]: [
    ...BASE_SUPPORTED_POST_METRICS,
    "reach",
    "videoWatchTime",
  ],
  // Facebook organic: adds clicks/CTR, link clicks (the link subset of clicks)
  // and reach.
  [ResourceChannel.FACEBOOK_ORGANIC]: [
    ...BASE_SUPPORTED_POST_METRICS,
    "clicks",
    "clickthroughRate",
    "linkClicks",
    "reach",
  ],
  // LinkedIn organic: clicks/CTR, but none of the new post metrics.
  [ResourceChannel.LINKED_IN_ORGANIC]: [
    ...BASE_SUPPORTED_POST_METRICS,
    "clicks",
    "clickthroughRate",
  ],
  // YouTube: engagement counts only.
  [ResourceChannel.YOUTUBE]: BASE_SUPPORTED_POST_METRICS,
};

// Metrics we only have an organic value for (no sponsored twin in the API).
// In the sponsored metric view the cards fall back to the organic total, so
// these must be hidden there rather than showing an organic number under the
// "Paid" tab.
export const ORGANIC_ONLY_POST_METRICS: readonly MetricKey[] = [
  "reach",
  "saves",
  "linkClicks",
  "videoWatchTime",
];

// Per-post metrics that exist as an organic/sponsored pair on the Post payload
// (`organicImpressions` / `sponsoredImpressions`, …). Mirrors the backend
// POST_METRIC_ORGANIC_PAID_SPLIT (social_media/utils.py), which rewrites the
// sort column for the same views so a Paid post card ranks by the number it
// shows. The post-grain sibling of ORGANIC_PAID_BREAKDOWN_METRICS: reach splits
// at page grain (Instagram) but is organic-only per post.
export const POST_SPLIT_METRICS = [
  "impressions",
  "clicks",
  "comments",
  "reactions",
  "shares",
  "engagement",
  "engagementRate",
  "clickthroughRate",
] as const satisfies readonly MetricKey[];

export function postMetricSupportsSplit(metric: string | undefined): boolean {
  return (
    metric !== undefined &&
    (POST_SPLIT_METRICS as readonly string[]).includes(metric)
  );
}

// Sort metrics the post card HIDES under Paid: the organic-only post metrics
// and the story-only ones (stories are never boosted, so a story-metric sort
// under Paid yields nothing). Metrics outside this set but without a paid twin
// (website-joined, calculated) stay visible as totals, so ranking by them is
// still checkable against the cards.
export function postMetricHiddenUnderPaid(metric: string | undefined): boolean {
  if (!metric) return false;
  return (
    (ORGANIC_ONLY_POST_METRICS as readonly string[]).includes(metric) ||
    (socialMediaStoryOnlyMetrics as readonly string[]).includes(metric)
  );
}

// Channels whose posts can be boosted (`isSponsored`). TikTok and YouTube posts
// are never sponsored, so a Paid view over them alone is always empty and the
// Organic view equals the total — the widget config disables the split there.
export const POST_SPLIT_CAPABLE_CHANNELS: readonly ResourceChannel[] = [
  ResourceChannel.FACEBOOK_ORGANIC,
  ResourceChannel.INSTAGRAM_ORGANIC,
  ResourceChannel.LINKED_IN_ORGANIC,
];

export function postSplitAvailableForChannels(
  channels: readonly ResourceChannel[],
): boolean {
  // With no channels known yet we don't gate, to avoid disabling the options
  // before the resources query has resolved.
  if (channels.length === 0) return true;
  return channels.some((c) => POST_SPLIT_CAPABLE_CHANNELS.includes(c));
}

// The posts endpoint's `is_sponsored` filter for a data-type view: Paid lists
// boosted posts only, Organic lists unboosted posts only, Total lists both.
// The backend also keys its split-aware ordering off this same param, so the
// rows, the sort column and the displayed value can't disagree.
export function metricViewToIsSponsored(
  view: SocialMediaMetricView | undefined,
): boolean | undefined {
  if (view === "paid") return true;
  if (view === "organic") return false;
  return undefined;
}

// On a post list this control filters rows as well as picking each card's
// figures. Stored values stay total/organic/paid so saved filters, shared links
// and widget configs keep working.
export const POST_PROMOTION_LABEL = "Promotion";
export const POST_PROMOTION_OPTION_LABELS: Readonly<
  Record<PostPromotionView, string>
> = {
  total: "All posts",
  organic: "Not promoted",
  paid: "Promoted: paid metrics",
  promotedTotal: "Promoted: all metrics",
};

export function promotionViewToMetricView(
  view: PostPromotionView | undefined,
): SocialMediaMetricView | undefined {
  return view === "promotedTotal" ? "total" : view;
}

// The posts endpoint's row-only `promoted_only` filter. Undefined (not false)
// when off so the param is dropped and existing query keys stay unchanged.
export function promotionViewToPromotedOnly(
  view: PostPromotionView | undefined,
): true | undefined {
  return view === "promotedTotal" ? true : undefined;
}

// A Facebook story that is Instagram's "share to Facebook" copy reports 0
// views and reach — Meta counts both on the Instagram story — so these two
// tiles show the linked Instagram story's numbers, labelled as such. Its FB
// reactions/interactions are real FB engagement and stay the copy's own.
const CROSSPOST_METRICS: readonly MetricKey[] = ["reach", "impressions"];

export function isCrosspostMetric(post: Post, metric: MetricKey): boolean {
  return Boolean(post.crosspostSource) && CROSSPOST_METRICS.includes(metric);
}

export interface CrosspostLink {
  pk: number;
  label: string;
  icon: "instagram" | "facebook";
  tooltip: string;
}

// The badge linking a shared story and its original, either way round.
export function crosspostLink(post: Post): CrosspostLink | null {
  if (post.crosspostSource) {
    return {
      pk: post.crosspostSource.pk,
      label: "Also on Instagram",
      icon: "instagram",
      tooltip: crosspostTooltip(post.crosspostSource),
    };
  }
  const copies = post.crosspostCopies ?? [];
  if (copies.length) {
    // Normally one copy; more only if several Pages share this IG account.
    // The badge links the first and the tooltip names them all.
    const pages = [...new Set(copies.map((copy) => copy.resourceName))];
    return {
      pk: copies[0].pk,
      label: "Also on Facebook",
      icon: "facebook",
      tooltip: `Also shared to Facebook (${pages.join(", ")}). Meta counts the shared story's views and reach here, on Instagram, and reports 0 for them on Facebook, so the Facebook card shows these numbers too.`,
    };
  }
  return null;
}

export function reloadPreviewConflictMessage(code: string | undefined): string {
  switch (code) {
    case "resource_unhealthy":
      return "This account is disconnected — reconnect it to refresh.";
    case "nothing_to_refetch":
      return "Nothing to refresh — assets are already cached.";
    case "unsupported_channel":
      return "Reload isn't supported for this channel.";
    case "not_collaborator":
      return "Instagram no longer lists this post for this account. It may have been deleted, or the collab ended.";
    case "platform_stalled":
      return "Instagram didn't return this post — try again in a few minutes.";
    default:
      // A reload still running, or one that ended in a collab 409 (which
      // keeps the stamp); a successful reload clears it.
      return "A reload of this post was started a moment ago — try again in a few minutes.";
  }
}

export function collabPublisher(post: Pick<Post, "extra">): string | null {
  const username = post.extra?.collabUsername;
  return username ? `@${username}` : null;
}

export function collabPostTooltip(post: Pick<Post, "extra">): string {
  const publisher = collabPublisher(post) ?? "Another account";
  return `${publisher} published this post and invited this account as a collaborator. Its numbers are for the whole post, across every collaborating account's audience, not just this one's. Post totals include it once. Reach isn't available, impressions (and the rates built on them) only for Reels and videos, and counts the publisher hides aren't shown.`;
}

export function collabBadge(
  post: Post,
): { label: string; tooltip: string } | null {
  if (!post.isCollab) return null;
  const publisher = collabPublisher(post);
  return {
    label: publisher ? `Collab with ${publisher}` : "Collab",
    tooltip: collabPostTooltip(post),
  };
}

export function crosspostTooltip(source: CrosspostSource): string {
  return `Also posted on Instagram (${source.resourceName}). Meta counts views and reach for stories shared from Instagram on the Instagram story, so Reach and Impressions here are identical to its Instagram card. Replies, interactions, reactions and shares are from Facebook.`;
}

export function crosspostMetricTooltip(
  source: CrosspostSource,
  label: string | undefined,
): string {
  return `${label} of the Instagram story (${source.resourceName}) this was shared from. The numbers are identical to its Instagram card. Facebook reports 0 for shared stories.`;
}

// One post's value for the active view: the `organic*` / `sponsored*` twin
// when the payload carries it, else the combined value (metrics with no paid
// counterpart fall back to their single value). Shared by the post card and the
// widget's "least performing" filter so both read the same column.
export function resolvePostMetricValue(
  post: Post,
  metric: MetricKey,
  view: SocialMediaMetricView | undefined,
): number | null | undefined {
  if (post.crosspostSource && view !== "paid") {
    if (metric === "reach") return post.crosspostSource.reach;
    if (metric === "impressions") return post.crosspostSource.impressions;
  }
  if (view === undefined || view === "total") {
    return post[metric as keyof Post] as number | null | undefined;
  }
  const prefix = view === "organic" ? "organic" : "sponsored";
  const key = `${prefix}${upperFirst(metric)}` as keyof Post;
  return (key in post ? post[key] : post[metric as keyof Post]) as
    | number
    | null
    | undefined;
}

const COLLAB_UNSPLIT_METRICS = [
  "impressions",
  "engagementRate",
  "clickthroughRate",
];

// A paid 0 reads as "no split": stored zeroes can't tell missing data from a
// measured zero. A collab post's views already include the paid views (see
// `collab_views_include_paid` in the backend), so its organic impressions are
// the total, not the organic part, and the rates that divide by them are off too.
export function postMetricSplit(
  post: Post,
  metric: string,
): { sponsored: number; organic: number | null } | null {
  const sponsoredKey = `sponsored${upperFirst(metric)}` as keyof Post;
  const organicKey = `organic${upperFirst(metric)}` as keyof Post;
  if (!post.isSponsored || !(sponsoredKey in post) || !(organicKey in post)) {
    return null;
  }
  if (post.isCollab && COLLAB_UNSPLIT_METRICS.includes(metric)) return null;
  const sponsored = post[sponsoredKey];
  const organic = post[organicKey];
  if (typeof sponsored !== "number" || sponsored === 0) return null;
  return {
    sponsored,
    organic: typeof organic === "number" ? organic : null,
  };
}

export function isPostMetricSupportedForChannel(
  metric: MetricKey,
  channel: ResourceChannel,
  view?: SocialMediaMetricView,
): boolean {
  if (view === "paid" && ORGANIC_ONLY_POST_METRICS.includes(metric)) {
    return false;
  }
  // Calculated metrics are channel-agnostic, so they stay available everywhere.
  if (isCalculatedMetric(metric)) {
    return true;
  }
  const supported = SUPPORTED_POST_METRICS_BY_CHANNEL[channel];
  // A channel not yet wired up isn't gated (avoids hiding everything for a
  // future channel); known channels gate to their supported set.
  return supported ? supported.includes(metric) : true;
}

export function useSocialMediaPostMetrics() {
  const calculatedMetricKeys = useCalculatedMetricKeys(
    MetricType.SOCIAL_MEDIA,
    socialMediaPostMetrics,
  );
  return computed(() => [
    ...socialMediaPostMetrics,
    ...calculatedMetricKeys.value,
  ]);
}

export type SocialMediaPostMetric =
  | (typeof socialMediaPostMetrics)[number]
  | CalculatedMetricKey;

// A story card renders EITHER a regular post metric or a story-only metric.
// Kept as its own type (rather than widening SocialMediaPostMetric) so story
// keys don't leak into page/post-metric consumers that can't handle them.
export type SocialMediaStoryMetric =
  | (typeof socialMediaStoryMetrics)[number]
  | SocialMediaPostMetric;

export type SocialMediaPostComparisonMetric =
  | (typeof socialMediaPostComparisonMetrics)[number]
  | CalculatedMetricKey;

export const socialMediaPageMetrics = [
  "impressions",
  "reach",
  "clicks",
  "clickthroughRate",
  "followers",
  "engagement",
  "engagementRate",
  "shares",
  "comments",
  "reactions",
  "postsPublished",
] as const satisfies MetricKey[];

// Reach is available in the picker but OFF by default: it needs slow live
// platform API calls, so we don't fetch it until the user opts in. Every other
// page metric is selected by default.
export const socialMediaPageMetricsDefaultSelected =
  socialMediaPageMetrics.filter(
    (metric) => metric !== "reach",
  ) as SocialMediaPageMetric[];

export function useSocialMediaPageMetrics() {
  const calculatedMetricKeys = useCalculatedMetricKeys(
    MetricType.SOCIAL_MEDIA,
    socialMediaPageMetrics,
  );
  return computed(() => [
    ...socialMediaPageMetrics,
    ...calculatedMetricKeys.value,
  ]);
}

/**
 * The option list for a social "Sort by" dropdown whose visible list is the
 * viewer's own metric selection.
 *
 * On a normal visit it is exactly `visible`, so `validate-against-options`
 * can clamp a selection the viewer has since deselected. On a `?share=` visit
 * `visible` is the VIEWER's selection, not the sharer's, so the link's key is
 * appended when it is outside that selection but inside `domain` — the complete
 * set the panel can rank by, calculated metrics included once loaded. A key
 * outside the domain (a deleted calculated metric, a hand-edited link) is not
 * appended, so the same readiness-gated clamp removes it instead of the label
 * naming a metric the backend silently ignores.
 *
 * The append must stay behind both conditions: a list that always contains the
 * current selection is self-satisfying, and the clamp could never fire.
 *
 * `fallback` is what the list becomes when it would otherwise be EMPTY — a
 * viewer whose selection holds only metrics the panel cannot rank by (page-only
 * ones, for the account tab). The dropdown declines to clamp against an empty
 * list, so without a fallback a stale key would survive exactly there.
 */
export function sortOptionsWithSharedKey<T extends string>(
  visible: readonly T[],
  current: string,
  isSharedUrl: boolean,
  domain: readonly string[],
  fallback: readonly T[] = [],
): readonly T[] {
  const options =
    !isSharedUrl ||
    (visible as readonly string[]).includes(current) ||
    !domain.includes(current)
      ? visible
      : [...visible, current as T];
  return options.length ? options : fallback;
}

// Metrics the account-summary "Top posts" panel can rank by: post metrics that
// are also page metrics, because its option list is the stat-card selection
// (page metrics) intersected with the post metrics. Narrower than
// socialMediaPostSortKeys, which is the Posts tab's domain.
export const socialMediaOverviewPostSortMetrics: readonly SocialMediaPostSortKey[] =
  socialMediaPostMetrics.filter((metric) =>
    (socialMediaPageMetrics as readonly string[]).includes(metric),
  );

// Default page-metric selection: reach is opt-in (excluded — it triggers slow
// live fetches), calculated metrics are default-on. Single source of truth so
// the account tab and the ActionBar metric selector can't diverge.
export function useSocialMediaPageDefaultMetrics() {
  const calculatedMetricKeys = useCalculatedMetricKeys(
    MetricType.SOCIAL_MEDIA,
    socialMediaPageMetrics,
  );
  return computed(() => [
    ...socialMediaPageMetricsDefaultSelected,
    ...calculatedMetricKeys.value,
  ]);
}

export type SocialMediaPageMetric =
  | (typeof socialMediaPageMetrics)[number]
  | CalculatedMetricKey;

export const socialMediaPostSortKeys = [
  "publishedAt",
  ...socialMediaPostMetrics,
  // Story-only metrics are sortable on the post-card widget too (rank stories
  // by Exits/Replies/etc.). They're real Post columns, so keyof Post holds.
  ...socialMediaStoryOnlyMetrics,
] as const satisfies (keyof Post)[];

export function useSocialMediaPostSortKeys() {
  const socialMediaPostMetrics = useSocialMediaPostMetrics();
  return computed(
    () =>
      [
        ...socialMediaPostSortKeys,
        ...socialMediaPostMetrics.value.filter(isCalculatedMetric),
      ] as const satisfies (keyof Post)[],
  );
}

export type SocialMediaPostSortKey =
  | (typeof socialMediaPostSortKeys)[number]
  | CalculatedMetricKey;

export interface PageMetricByDateAndPlatformDataset {
  label: string;
  channel: SocialMediaResourceChannel;
  resource: string | null;
  resourceId: number | null;
  entityKey: string;
  data: (number | null)[];
  period: TemporalPeriodsType;
}

export interface PageMetricByDateAndPlatform {
  labels: string[];
  datasets: PageMetricByDateAndPlatformDataset[];
}

export interface OrganicPaidBreakdownEntry {
  organic: (number | null)[];
  paid: (number | null)[];
}

export type OrganicPaidBreakdown = Record<string, OrganicPaidBreakdownEntry>;

// Per-resource breakdown: metric -> resourceName -> { organic, paid }
export type PerResourceBreakdown = Record<
  string,
  Record<string, OrganicPaidBreakdownEntry>
>;

export type PageMetricsByDateAndPlatform = Record<
  SocialMediaPageMetric,
  PageMetricByDateAndPlatform
> & {
  organicPaidBreakdown?: OrganicPaidBreakdown;
  perResourceBreakdown?: PerResourceBreakdown;
};
export type TemporalPageMetricsByDateAndPlatform = Record<
  TemporalPeriodsType,
  PageMetricsByDateAndPlatform
>;

function listPageMetricsByDateAndPlatform({
  queryKey: [, params],
}: QueryFunctionContext<
  {
    timeUnit: TimeUnit;
  } & SeparateResourcesFilter &
    TemporalDataFilter &
    ResourcesDatafilter &
    PublicDashboardWidgetFilters
>): Promise<TemporalPageMetricsByDateAndPlatform> {
  return api
    .get("/social_media/page_metrics_by_date_and_platform/", { params })
    .then((response) => response.data);
}

export function usePageMetricsByDateAndPlatformQuery({
  selectedResourceIds,
  temporalFilter,
  timeUnit = ref(TimeUnit.DAY),
  metrics,
  enabled,
  useGlobalFilters = true,
}: {
  selectedResourceIds?: Ref<number[]>;
  temporalFilter?: Ref<DateRangeSelection>;
  timeUnit?: Ref<TimeUnit>;
  // Sent as `fields`: the series to compute, with their organic/paid splits.
  metrics?: Ref<string[]>;
  enabled?: Ref<boolean>;
  useGlobalFilters?: boolean;
} = {}) {
  const queryKey = [
    "pageMetricsByDateAndPlatformQuery",
    {
      ...useCalculatedResources(selectedResourceIds, useGlobalFilters),
      ...useDetachedTemporalFilter(temporalFilter, useGlobalFilters),
      ...useDataFilters(["separateResources"], useGlobalFilters),
      ...(metrics && useMetricFilter(metrics)),
      ...usePublicDashboardWidgetQueryFilters(),
      timeUnit,
    },
  ] as const;
  return reactive(
    useQuery({ queryKey, queryFn: listPageMetricsByDateAndPlatform, enabled }),
  );
}
export const usePageMetricsByDateAndPlatformQueryWithMetrics = {
  query: usePageMetricsByDateAndPlatformQuery,
  metrics: socialMediaPageMetrics,
};

// Deduplicated-reach fields + caveat flags shared by the topline metric payload,
// the reach-only payload, and the stat-card period. Kept in one place so a field
// rename/addition can't silently drift between the three consumers.
export interface ReachCaveatFields {
  totalOrganicReach?: number | null;
  totalSponsoredReach?: number | null;
  reachIsCapped?: boolean;
  reachIsMultiPageEstimate?: boolean;
  // A refused reach fetch, in one of two shapes: a multi-page sum missing one
  // or more pages (under-counts, opposite of the multi-page over-count), or a
  // single page with no value at all (reach is null). Keyed apart on whether a
  // value is present — see reachPartialTooltip.
  reachIsPartial?: boolean;
}

// The two periods are flagged independently by the backend, and both consumers
// (the stat-card widget core and the social account tab) need the same two
// answers. Kept here as pure functions so they can be tested without mounting
// either component.
export function reachIsPartialInEitherPeriod(
  current: ReachCaveatFields | undefined,
  previous: ReachCaveatFields | undefined,
): boolean {
  return !!current?.reachIsPartial || !!previous?.reachIsPartial;
}

// A delta against a refused comparison would invent a trend out of a failed
// fetch, so the previous value must be dropped rather than rendered.
export function reachComparisonWasRefused(
  previous: ReachCaveatFields | undefined,
): boolean {
  return !!previous?.reachIsPartial;
}

export interface PageMetricsByComparisonPeriod
  extends Record<SocialMediaPageMetric, number | null>, ReachCaveatFields {
  label: "current" | "previous";
  totalOrganicImpressions?: number | null;
  totalSponsoredImpressions?: number | null;
  totalOrganicClicks?: number | null;
  totalSponsoredClicks?: number | null;
  totalOrganicComments?: number | null;
  totalSponsoredComments?: number | null;
  totalOrganicReactions?: number | null;
  totalSponsoredReactions?: number | null;
  totalOrganicShares?: number | null;
  totalSponsoredShares?: number | null;
  totalOrganicEngagement?: number | null;
  totalSponsoredEngagement?: number | null;
  followersIsEstimated?: boolean;
  followersEstimatedDate?: string | null;
  followersEstimatedChannels?: string[] | null;
}

export interface PageMetricsByComparison {
  currentPeriod: PageMetricsByComparisonPeriod;
  previousPeriod: PageMetricsByComparisonPeriod;
}

// Page metrics that carry an organic/paid split. Mirrors the backend
// ORGANIC_PAID_BREAKDOWN_METRICS map (social_media/utils.py). Each entry maps a
// metric to the scalar fields on PageMetricsByComparisonPeriod (used by stat
// cards) — the by-date `organicPaidBreakdown` payload is keyed by the same
// metric names for chart series.
export const ORGANIC_PAID_BREAKDOWN_METRICS = {
  impressions: {
    organic: "totalOrganicImpressions",
    paid: "totalSponsoredImpressions",
  },
  // Reach splits organic/paid on Instagram only (Facebook "Viewers" and other
  // channels are total-only). This is a PAGE-level split — post-level reach is
  // organic-only (see ORGANIC_ONLY_POST_METRICS), which is a separate concern.
  reach: { organic: "totalOrganicReach", paid: "totalSponsoredReach" },
  clicks: { organic: "totalOrganicClicks", paid: "totalSponsoredClicks" },
  comments: { organic: "totalOrganicComments", paid: "totalSponsoredComments" },
  reactions: {
    organic: "totalOrganicReactions",
    paid: "totalSponsoredReactions",
  },
  shares: { organic: "totalOrganicShares", paid: "totalSponsoredShares" },
  engagement: {
    organic: "totalOrganicEngagement",
    paid: "totalSponsoredEngagement",
  },
} as const satisfies Partial<
  Record<
    SocialMediaPageMetric,
    {
      organic: keyof PageMetricsByComparisonPeriod;
      paid: keyof PageMetricsByComparisonPeriod;
    }
  >
>;

export type OrganicPaidRawMetric = keyof typeof ORGANIC_PAID_BREAKDOWN_METRICS;

// Rate metrics that can be split by computing the ratio from their split
// components (e.g. organic CTR = organic clicks / organic impressions). The
// comparison endpoint returns the components but not split rates, so we derive
// them client-side.
export const ORGANIC_PAID_RATE_METRICS = {
  clickthroughRate: { numerator: "clicks", denominator: "impressions" },
  engagementRate: { numerator: "engagement", denominator: "impressions" },
} as const satisfies Partial<
  Record<
    SocialMediaPageMetric,
    { numerator: OrganicPaidRawMetric; denominator: OrganicPaidRawMetric }
  >
>;

export type OrganicPaidRateMetric = keyof typeof ORGANIC_PAID_RATE_METRICS;
export type OrganicPaidSplittableMetric =
  | OrganicPaidRawMetric
  | OrganicPaidRateMetric;

function isRawSplitMetric(
  metric: MetricKey | undefined,
): metric is OrganicPaidRawMetric {
  return metric !== undefined && metric in ORGANIC_PAID_BREAKDOWN_METRICS;
}

function isRateSplitMetric(
  metric: MetricKey | undefined,
): metric is OrganicPaidRateMetric {
  return metric !== undefined && metric in ORGANIC_PAID_RATE_METRICS;
}

// A metric supports an organic/paid split if it's a raw split metric or a rate
// derived entirely from split components (CTR, engagement rate).
export function pageMetricSupportsSplit(
  metric: MetricKey | undefined,
): metric is OrganicPaidSplittableMetric {
  return isRawSplitMetric(metric) || isRateSplitMetric(metric);
}

// Shared message for when an organic/paid view is active but a metric has no
// such breakdown and falls back to the total. Used by the topline and the
// stat-card widget so the wording stays identical.
export function organicPaidFallbackTooltip(
  view: SocialMediaMetricView,
): string {
  return `No ${view} breakdown available for this metric — showing the total.`;
}

// Which channels supply a PAGE-level organic/paid split per metric. Mirrors the
// backend: Facebook + Instagram split impressions and their engagement fields;
// reach splits on Instagram only. A channel absent from a metric's list still
// contributes its total but has no split for it, so a selection mixing both
// must warn (see selectionMixesSplittableChannels). Kept deliberately narrow —
// a metric not listed here is treated as "no channel splits it".
const PAGE_SPLIT_CAPABLE_CHANNELS_BY_METRIC: Partial<
  Record<OrganicPaidRawMetric, readonly ResourceChannel[]>
> = {
  impressions: [
    ResourceChannel.FACEBOOK_ORGANIC,
    ResourceChannel.INSTAGRAM_ORGANIC,
    ResourceChannel.LINKED_IN_ORGANIC,
  ],
  reach: [ResourceChannel.INSTAGRAM_ORGANIC],
  clicks: [ResourceChannel.FACEBOOK_ORGANIC, ResourceChannel.LINKED_IN_ORGANIC],
  comments: [
    ResourceChannel.FACEBOOK_ORGANIC,
    ResourceChannel.LINKED_IN_ORGANIC,
  ],
  reactions: [
    ResourceChannel.FACEBOOK_ORGANIC,
    ResourceChannel.LINKED_IN_ORGANIC,
  ],
  shares: [ResourceChannel.FACEBOOK_ORGANIC, ResourceChannel.LINKED_IN_ORGANIC],
  engagement: [
    ResourceChannel.FACEBOOK_ORGANIC,
    ResourceChannel.LINKED_IN_ORGANIC,
  ],
};

// True when an organic/paid view is active and the selected channels MIX one
// that can split this metric with one that can't — so the shown split silently
// omits (or totals-in) the non-splitting channels and the user must be warned.
// This is stricter than the per-metric didFallback check, which misses the case
// where a splitting channel makes the aggregate non-null while a non-splitting
// channel is quietly folded into the total.
export function selectionMixesSplittableChannels(
  metric: MetricKey | undefined,
  view: SocialMediaMetricView,
  channels: readonly ResourceChannel[],
): boolean {
  if (view === "total" || !isRawSplitMetric(metric)) return false;
  const capable = PAGE_SPLIT_CAPABLE_CHANNELS_BY_METRIC[metric] ?? [];
  const hasCapable = channels.some((c) => capable.includes(c));
  const hasIncapable = channels.some((c) => !capable.includes(c));
  return hasCapable && hasIncapable;
}

// Whether the organic/paid split is available for a metric given the selected
// channels — true if at least one selected channel can split it (e.g. reach
// splits on Instagram but not Facebook). Used to disable the paid/organic
// data-type options for selections where no channel supports the split (with
// no channels selected we don't gate, to avoid disabling before resources load).
export function pageMetricCanSplitForChannels(
  metric: MetricKey | undefined,
  channels: readonly ResourceChannel[],
): boolean {
  if (!isRawSplitMetric(metric)) return false;
  if (channels.length === 0) return true;
  const capable = PAGE_SPLIT_CAPABLE_CHANNELS_BY_METRIC[metric] ?? [];
  return channels.some((c) => capable.includes(c));
}

// Tooltip for the mixed-channel case (some selected channels can't split this
// metric, so their totals are folded in). Distinct from the full-fallback
// wording because here SOME channels do show a real split.
export function organicPaidMixedChannelTooltip(): string {
  return "Some selected channels don't report an organic/paid breakdown for this metric — their total is included.";
}

// Reach is deduplicated (unique people); the platforms cap how long a span a
// single deduplicated figure can cover (30 days on Instagram, 93 on Facebook).
// For longer ranges the shown reach is the most-recent full-window total,
// flagged with this note.
export function reachCappedTooltip(): string {
  return "Reach reflects the most recent period the platform can deduplicate — shorter than your selected range.";
}

// Reach across multiple pages is summed and over-counts anyone reached on more
// than one page (unique people can't be deduplicated across pages).
export function reachMultiPageTooltip(): string {
  return "Approximate — combines reach across pages, so people reached on more than one page are counted multiple times.";
}

// A reach fetch the platform refused. For a multi-page sum the shown figure
// omits those pages and under-counts; for a single page there is no figure at
// all — which must not read as "this page had no reach", the ambiguity that
// made a production reach incident hard to diagnose.
export function reachPartialTooltip(hasValue = true): string {
  return hasValue
    ? "Incomplete — one or more pages couldn't be loaded, so this reach is lower than the true figure."
    : "Reach couldn't be loaded from the platform — this isn't zero reach. Try again shortly.";
}

// When only the COMPARISON period was refused, the displayed figure is fine —
// saying it's "lower than the true figure" would be actively wrong. The
// missing half is the previous period, so there's no change to show.
//
// `currentCaveat` carries any caveat that still applies to the CURRENT figure
// (multi-page over-count, capped window, organic/paid fallback). Without it a
// previous-only refusal would suppress those and call an approximate or
// truncated figure "complete".
export function reachComparisonPartialTooltip(currentCaveat = ""): string {
  const comparison =
    "The previous period's reach couldn't be loaded, so no change is shown.";
  return currentCaveat
    ? `${comparison} ${currentCaveat}`
    : `${comparison} The figure above is complete.`;
}

// One Pages-table reach cell. `previousValue` is undefined when there is no
// delta to show, so the table's change indicator stays hidden.
export interface PageReachCell {
  value: number | null;
  previousValue?: number | null;
  caveat: string | null;
}

function withComparisonCaveat(
  currentCaveat: string | null,
  comparisonRefused: boolean,
  currentRefused: boolean,
): string | null {
  return comparisonRefused && !currentRefused
    ? reachComparisonPartialTooltip(currentCaveat ?? "")
    : currentCaveat;
}

export function pageReachCell(
  reach: PageReachForResource | undefined,
): PageReachCell {
  if (!reach) return { value: null, caveat: null };
  const currentCaveat = reach.reachIsPartial
    ? reachPartialTooltip(false)
    : reach.reachIsCapped
      ? reachCappedTooltip()
      : null;
  const comparisonRefused = !!reach.comparison?.reachIsPartial;
  return {
    value: reach.reach,
    previousValue:
      reach.comparison && !comparisonRefused
        ? reach.comparison.reach
        : undefined,
    caveat: withComparisonCaveat(
      currentCaveat,
      comparisonRefused,
      reach.reachIsPartial,
    ),
  };
}

function sumReach(periods: PageReachForResourcePeriod[]): number | null {
  const values = periods
    .map((period) => period.reach)
    .filter((value) => value !== null);
  return values.length ? values.reduce((a, b) => a + b, 0) : null;
}

// The "All accounts" reach: a sum of the listed pages, so it carries the same
// caveats as the multi-page stat card.
export function pageReachTotalCell(
  reaches: PageReachForResource[],
): PageReachCell {
  const value = sumReach(reaches);
  const contributing = reaches.filter((reach) => reach.reach !== null).length;
  const currentRefused = reaches.some((reach) => reach.reachIsPartial);
  const currentCaveat = currentRefused
    ? reachPartialTooltip(value !== null)
    : contributing > 1
      ? reachMultiPageTooltip()
      : reaches.some((reach) => reach.reachIsCapped)
        ? reachCappedTooltip()
        : null;
  const comparisons = reaches.flatMap((reach) =>
    reach.comparison ? [reach.comparison] : [],
  );
  const comparisonRefused = comparisons.some(
    (comparison) => comparison.reachIsPartial,
  );
  return {
    value,
    previousValue:
      comparisons.length && !comparisonRefused
        ? sumReach(comparisons)
        : undefined,
    caveat: withComparisonCaveat(
      currentCaveat,
      comparisonRefused,
      currentRefused,
    ),
  };
}

// Append a "(total)" marker to a chart series label when, under an active
// organic/paid view, the series actually fell back to the total — i.e. the
// per-dataset breakdown resolver returned null (rate metric, non-split metric,
// or a dataset with no real split for this period). Driven by the resolved
// series rather than the metric type so the label can't disagree with the data
// (a raw metric whose dataset has no split still gets marked). The chart can't
// show a tooltip, so it suffixes the label; the stat card warns instead.
export function organicPaidChartLabel(
  label: string,
  view: SocialMediaMetricView,
  breakdownSeries: (number | null)[] | null,
): string {
  return view !== "total" && breakdownSeries === null
    ? `${label} (total)`
    : label;
}

export type OrganicPaidSplitField =
  (typeof ORGANIC_PAID_BREAKDOWN_METRICS)[OrganicPaidRawMetric][
    | "organic"
    | "paid"];

// Period shape consumed by resolvePageMetricValue: any record that exposes the
// metric columns plus the totalOrganic*/totalSponsored* split fields. Both the
// topline (PageMetricsByComparisonPeriod) and the StatCard widget's StatCardData
// period satisfy this structurally, so neither caller needs a cast.
export type OrganicPaidResolvablePeriod = Partial<
  Record<SocialMediaPageMetric | OrganicPaidSplitField, number | null>
>;

// Resolve a stat-card scalar value for the chosen data-type view. "total"
// returns the combined metric; "organic"/"paid" return the matching split
// field, or (for rate metrics) the ratio of the split components. Metrics with
// no split resolve to the total (callers decide whether to show a warning).
export function resolvePageMetricValue(
  period: OrganicPaidResolvablePeriod | undefined,
  metric: MetricKey,
  view: SocialMediaMetricView,
): number | null | undefined {
  if (!period) return undefined;
  if (view === "total") {
    return period[metric as SocialMediaPageMetric];
  }
  if (isRawSplitMetric(metric)) {
    return period[ORGANIC_PAID_BREAKDOWN_METRICS[metric][view]];
  }
  if (isRateSplitMetric(metric)) {
    const { numerator, denominator } = ORGANIC_PAID_RATE_METRICS[metric];
    const num = period[ORGANIC_PAID_BREAKDOWN_METRICS[numerator][view]];
    const den = period[ORGANIC_PAID_BREAKDOWN_METRICS[denominator][view]];
    // Guard missing components precisely: only null/undefined is "no data". A
    // real den === 0 still yields null here (n/0 is undefined) but isn't
    // conflated with a missing denominator.
    if (
      num === null ||
      num === undefined ||
      den === null ||
      den === undefined
    ) {
      return null;
    }
    if (den === 0) return null;
    return num / den;
  }
  return period[metric as SocialMediaPageMetric];
}

export interface ResolvedPageMetric {
  // The value to display: the split for the active view, or the total when the
  // metric/period has no split (then `didFallback` is true).
  value: number | null | undefined;
  // True when an organic/paid view is active but this period had no split and
  // resolved to the total instead. Callers warn on this.
  didFallback: boolean;
}

// Resolve one period's value for the active view, reporting whether it fell back
// to the total. The single source of truth both the topline stat cards and the
// StatCard widget consume — keeping resolution + warning logic from diverging.
// A genuine 0 split is kept (only null/undefined triggers the fallback).
export function resolvePageMetricForPeriod(
  period: OrganicPaidResolvablePeriod | undefined,
  metric: MetricKey,
  view: SocialMediaMetricView,
): ResolvedPageMetric {
  if (view === "total") {
    return {
      value: resolvePageMetricValue(period, metric, "total"),
      didFallback: false,
    };
  }
  // A non-splittable metric (e.g. followers) under an organic/paid view shows
  // the total and IS flagged as a fallback, so callers warn (matches #7817 —
  // "no-split metrics show the total with an info tooltip").
  if (!pageMetricSupportsSplit(metric)) {
    return {
      value: resolvePageMetricValue(period, metric, "total"),
      didFallback: true,
    };
  }
  const split = resolvePageMetricValue(period, metric, view);
  if (split !== null && split !== undefined) {
    return { value: split, didFallback: false };
  }
  return {
    value: resolvePageMetricValue(period, metric, "total"),
    didFallback: true,
  };
}

export interface ResolvedPageMetricComparison extends ResolvedPageMetric {
  previousValue: number | null | undefined;
}

// Resolve current + comparison values together. `didFallback` is true when
// EITHER period fell back to the total, so the warning fires for an asymmetric
// split (e.g. current has a paid breakdown but the comparison period doesn't —
// otherwise the card would show split-vs-total as a real delta with no warning).
export function resolvePageMetricComparison(
  currentPeriod: OrganicPaidResolvablePeriod | undefined,
  previousPeriod: OrganicPaidResolvablePeriod | undefined,
  metric: MetricKey,
  view: SocialMediaMetricView,
): ResolvedPageMetricComparison {
  const current = resolvePageMetricForPeriod(currentPeriod, metric, view);
  const previous = resolvePageMetricForPeriod(previousPeriod, metric, view);
  return {
    value: current.value,
    previousValue: previous.value,
    // Only flag the fallback when there's an actual total to show; an empty
    // total means the metric simply has no data and needs no warning.
    didFallback:
      (current.didFallback &&
        current.value !== null &&
        current.value !== undefined) ||
      (previous.didFallback &&
        previous.value !== null &&
        previous.value !== undefined),
  };
}

// Extracts the perResourceBreakdown off a by-date period object, which the
// generated chart-payload types don't model. Accepts unknown so chart cores can
// pass the loosely-typed period without casting at each call site.
export function getPerResourceBreakdown(
  period: unknown,
): PerResourceBreakdown | undefined {
  if (
    period &&
    typeof period === "object" &&
    "perResourceBreakdown" in period
  ) {
    return (period as { perResourceBreakdown?: PerResourceBreakdown })
      .perResourceBreakdown;
  }
  return undefined;
}

// perResourceBreakdown is keyed by resource pk when separating resources (names
// aren't unique across a brand's pages), otherwise by platform (= channel).
export function perResourceBreakdownKey(dataset: {
  resourceId?: number | null;
  channel?: string;
}): string | undefined {
  return typeof dataset.resourceId === "number"
    ? String(dataset.resourceId)
    : dataset.channel;
}

// Chart-series analog of resolvePageMetricValue for the by-date payload. Picks
// the organic/paid time-series for one chart dataset from perResourceBreakdown,
// matched on the dataset's resource pk (separateResources) or channel/platform
// (aggregated). Returns null when there's no split to apply — the metric isn't
// splittable, the view is total, no matching breakdown entry exists, or the
// matched series is entirely null — and the caller keeps the dataset's total.
export function resolveDatasetBreakdownSeries(
  perResourceBreakdown: PerResourceBreakdown | undefined,
  dataset: {
    metric?: MetricKey;
    resourceId?: number | null;
    channel?: string;
  },
  view: SocialMediaMetricView,
): (number | null)[] | null {
  if (view === "total" || !perResourceBreakdown) return null;
  const metric = dataset.metric;
  // Charts only carry raw split series per day; rate metrics (CTR/ER) can't be
  // split here without per-day numerator/denominator series, so they stay total.
  if (!isRawSplitMetric(metric)) return null;
  const byKey = perResourceBreakdown[metric];
  if (!byKey) return null;
  const key = perResourceBreakdownKey(dataset);
  const entry = key ? byKey[key] : undefined;
  if (!entry) return null;
  const series = entry[view];
  // The backend creates a breakdown entry for every resource in the payload,
  // even when a field has no organic/paid split for it (e.g. Facebook only
  // splits impressions), yielding an all-null series. `?? ds.data` wouldn't
  // catch that (the array is truthy), so treat all-null as "no split" and fall
  // back to the total here instead of plotting a blank line.
  return series && series.some((v) => v !== null) ? series : null;
}

function listPageMetricsByComparison({
  queryKey: [, params],
}: QueryFunctionContext<
  TemporalDataFilter & ResourcesDatafilter & PublicDashboardWidgetFilters
>): Promise<PageMetricsByComparison> {
  return api
    .get("/social_media/page_metrics_compared_to_previous_period/", { params })
    .then((response) => response.data);
}

export function usePageMetricsByComparisonQuery({
  selectedResourceIds,
  temporalFilter,
  metrics,
  enabled,
  useGlobalFilters = true,
}: {
  selectedResourceIds?: Ref<number[]>;
  temporalFilter?: Ref<DateRangeSelection>;
  // Sent as `fields`, so only these are computed. The organic/paid splits and
  // follower caveats the stat cards read come back with them.
  metrics?: Ref<string[]>;
  enabled?: Ref<boolean>;
  useGlobalFilters?: boolean;
} = {}) {
  const queryKey = [
    "pageMetricsByComparison",
    {
      ...useDetachedTemporalFilter(temporalFilter, useGlobalFilters),
      ...useCalculatedResources(selectedResourceIds, useGlobalFilters),
      ...(metrics && useMetricFilter(metrics)),
      ...usePublicDashboardWidgetQueryFilters(),
    },
  ] as const;
  return reactive(
    useQuery({ queryKey, queryFn: listPageMetricsByComparison, enabled }),
  );
}
export const usePageMetricsByComparisonQueryWithMetrics = {
  query: usePageMetricsByComparisonQuery,
  // Reach is served by its own query (usePageReachByComparisonQuery) because it
  // needs slow live platform API calls; excluding it here keeps the stat cards
  // fast and means reach is only fetched when its tile is shown.
  metrics: socialMediaPageMetrics.filter((metric) => metric !== "reach"),
};

export interface PageReachByComparisonPeriod extends ReachCaveatFields {
  label: "current" | "previous";
  reach?: number | null;
}

export interface PageReachByComparison {
  currentPeriod: PageReachByComparisonPeriod;
  previousPeriod: PageReachByComparisonPeriod;
}

function listPageReachByComparison({
  queryKey: [, params],
}: QueryFunctionContext<
  TemporalDataFilter & ResourcesDatafilter & PublicDashboardWidgetFilters
>): Promise<PageReachByComparison> {
  return api
    .get("/social_media/page_reach_compared_to_previous_period/", { params })
    .then((response) => response.data);
}

export function usePageReachByComparisonQuery({
  selectedResourceIds,
  temporalFilter,
  enabled,
  useGlobalFilters = true,
}: {
  selectedResourceIds?: Ref<number[]>;
  temporalFilter?: Ref<DateRangeSelection>;
  enabled?: Ref<boolean>;
  useGlobalFilters?: boolean;
} = {}) {
  const queryKey = [
    "pageReachByComparison",
    {
      ...useDetachedTemporalFilter(temporalFilter, useGlobalFilters),
      ...useCalculatedResources(selectedResourceIds, useGlobalFilters),
      ...usePublicDashboardWidgetQueryFilters(),
    },
  ] as const;
  return reactive(
    useQuery({ queryKey, queryFn: listPageReachByComparison, enabled }),
  );
}

export const usePageReachByComparisonQueryWithMetrics = {
  query: usePageReachByComparisonQuery,
  metrics: ["reach"] as const,
};

export interface PostMetricsByComparisonPeriod extends Partial<
  Record<SocialMediaPostComparisonMetric, number | null>
> {
  label: "current" | "previous";
  postsPublished?: number | null;
}

export interface PostMetricsByComparison {
  currentPeriod: PostMetricsByComparisonPeriod;
  previousPeriod: PostMetricsByComparisonPeriod;
}

function listPostMetricsByComparison({
  queryKey: [, params],
}: QueryFunctionContext<
  TemporalDataFilter &
    ResourcesDatafilter &
    TagsDataFilter &
    PublicDashboardWidgetFilters
>): Promise<PostMetricsByComparison> {
  return api
    .get("/social_media/post_metrics_compared_to_previous_period/", { params })
    .then((response) => response.data);
}

export function usePostMetricsByComparisonQuery({
  selectedResourceIds,
  includedTags,
  excludedTags,
  includedTagsLogic,
  temporalFilter,
  includeStories,
  enabled,
  useGlobalFilters = true,
}: {
  selectedResourceIds?: Ref<number[]>;
  includedTags?: Ref<number[]>;
  excludedTags?: Ref<number[]>;
  includedTagsLogic?: Ref<"AND" | "OR">;
  temporalFilter?: Ref<DateRangeSelection>;
  includeStories?: Ref<boolean | undefined>;
  enabled?: Ref<boolean>;
  useGlobalFilters?: boolean;
} = {}) {
  const queryKey = [
    "postMetricsByComparison",
    {
      ...useDetachedTemporalFilter(temporalFilter, useGlobalFilters),
      ...useCalculatedResources(selectedResourceIds, useGlobalFilters),
      ...useDetachedTagFilter(
        useGlobalFilters,
        includedTags,
        excludedTags,
        includedTagsLogic,
      ),
      ...usePublicDashboardWidgetQueryFilters(),
      // Sent only on explicit exclude (ref carries false); undefined → axios
      // drops it, so the backend applies its default (stories included).
      include_stories: includeStories,
    },
  ] as const;
  return reactive(
    useQuery({ queryKey, queryFn: listPostMetricsByComparison, enabled }),
  );
}

export const usePostMetricsByComparisonQueryWithMetrics = {
  query: usePostMetricsByComparisonQuery,
  metrics: socialMediaPostWidgetMetrics,
};

export type PageMetricsByResourceComparison = Partial<
  Record<SocialMediaPageMetric, number | null>
>;

export interface PageMetricsByResourceRow extends Record<
  SocialMediaPageMetric,
  number | null
> {
  resourceId: number;
  resourceName: string;
  channel: SocialMediaResourceChannel;
  // True if followers data was forward-filled from prior period due to missing data
  followersIsEstimated?: boolean;
  // The date of the last known followers data (only set if estimated)
  followersEstimatedDate?: string | null;
  comparison?: PageMetricsByResourceComparison;
}

export interface PageMetricsByResourceTotals extends Record<
  SocialMediaPageMetric,
  number | null
> {
  resourceId: null;
  resourceName: string;
  channel: null;
  // True if any resource has estimated followers data
  followersIsEstimated?: boolean;
  // The oldest estimated date from all resources (only set if estimated)
  followersEstimatedDate?: string | null;
  comparison?: PageMetricsByResourceComparison;
}

export interface PageMetricsByResourceResponse {
  results: PageMetricsByResourceRow[];
  totals: PageMetricsByResourceTotals;
}

function listPageMetricsByResource({
  queryKey: [, params],
}: QueryFunctionContext<
  TemporalDataFilter &
    PublicDashboardWidgetFilters & {
      ordering?: AscendingOrDescending<SocialMediaPageMetric | "Resource">;
      filterCriteria?: MetricFilterSet[];
    }
>): Promise<PageMetricsByResourceResponse> {
  const filterCriteria = filterCriteriaFormatter(params.filterCriteria);
  return api
    .get("/social_media/page_metrics_by_resource/", {
      params: { ...params, ...filterCriteria },
    })
    .then((response) => response.data);
}

export function usePageMetricsByResourceQuery({
  temporalFilter,
  ordering,
  filterCriteria,
  metrics,
  enabled,
  useGlobalFilters = true,
}: {
  temporalFilter?: Ref<DateRangeSelection>;
  ordering?: Ref<AscendingOrDescending<SocialMediaPageMetric | "Resource">>;
  filterCriteria?: Ref<MetricFilterSet[]>;
  // Sent as `fields`: the columns to compute.
  metrics?: Ref<string[]>;
  enabled?: Ref<boolean>;
  useGlobalFilters?: boolean;
} = {}) {
  const queryKey = [
    "pageMetricsByResource",
    {
      ...useDetachedTemporalFilter(temporalFilter, useGlobalFilters),
      ...usePublicDashboardWidgetQueryFilters(),
      ...(metrics && useMetricFilter(metrics)),
      ordering,
      filterCriteria,
    },
  ] as const;
  return reactive(
    useQuery({ queryKey, queryFn: listPageMetricsByResource, enabled }),
  );
}

export interface PageReachForResourcePeriod {
  reach: number | null;
  reachIsCapped: boolean;
  reachIsPartial: boolean;
}

export interface PageReachForResource extends PageReachForResourcePeriod {
  resourceId: number;
  resourceName: string;
  channel: SocialMediaResourceChannel;
  comparison?: PageReachForResourcePeriod;
}

export interface PageReachByResourceResponse {
  results: PageReachForResource[];
}

function listPageReachByResource({
  queryKey: [, params],
}: QueryFunctionContext<TemporalDataFilter>): Promise<PageReachByResourceResponse> {
  return api
    .get("/social_media/page_reach_by_resource/", { params })
    .then((response) => response.data);
}

export function usePageReachByResourceQuery({
  enabled,
}: {
  enabled?: Ref<boolean>;
} = {}) {
  const queryKey = [
    "pageReachByResource",
    { ...useDetachedTemporalFilter() },
  ] as const;
  return reactive(
    useQuery({ queryKey, queryFn: listPageReachByResource, enabled }),
  );
}

export interface OpenGraphData {
  link: string;
  url: string;
  type: string | null;
  title: string | null;
  mediaUrl: string | null;
  originalMediaUrl?: string | null;
  description: string | null;
  siteName: string | null;
}

export interface PostAsset {
  mediaType: string;
  mediaUrl: string;
  targetUrl: string;
  thumbnailMediaUrl?: string;
}

export enum PostAssetImageTypes {
  image = "image",
  photo = "photo",
  carousel_album__image = "carousel_album__image",
  album__image = "album__image",
  album__photo = "album__photo",
  multi_share__share = "multi_share__share",
  multi_share_no_end_card__share = "multi_share_no_end_card__share",
  profile_media = "profile_media",
  profile_media__photo = "profile_media__photo",
}

export enum PostAssetPageUpdateTypes {
  cover_photo = "cover_photo",
  profile_media = "profile_media",
}

export enum PostAssetVideoTypes {
  video = "video",
  reels = "reels",
  carousel_album__video = "carousel_album__video",
}

export enum PostAssetVideoThumbnailTypes {
  video_direct_response__video = "video_direct_response__video",
  animated_image_video__video = "animated_image_video__video",
}

export enum PostAssetLinkTypes {
  link = "link",
}

export enum PostAssetShareTypes {
  share = "share",
}

export enum VideoPostTypes {
  video = "video",
  reels = "reels",
}
export enum DocumentPostTypes {
  document = "document",
}

export enum CollectionPostTypes {
  multi_image = "multi_image",
  carousel = "carousel",
  album = "album",
  multi_share = "multi_share",
  multi_share_no_end_card = "multi_share_no_end_card",
}

export enum ImagePostTypes {
  image = "image",
  photo = "photo",
}

export enum VideoThumbnailPostTypes {
  video_inline = "video_inline",
  video_direct_response = "video_direct_response",
  animated_image_video = "animated_image_video",
}

export enum LinkPostTypes {
  link = "link",
  article = "article",
}

export enum SharePostTypes {
  share = "share",
}

export enum FeedPostTypes {
  feed = "feed",
}

export enum PageUpdatePostTypes {
  cover_photo = "cover_photo",
  profile_media = "profile_media",
}

export enum PostType {
  IMAGE = "image",
  VIDEO = "video",
  VIDEO_THUMBNAIL = "video_thumbnail",
  DOCUMENT = "document",
  COLLECTION = "collection",
  LINK = "link",
  SHARE = "share",
  FEED = "feed",
  PAGE_UPDATE = "page_update",
  UNKNOWN = "unknown",
}

// The Instagram story a Facebook story was shared from ("share to Facebook").
export interface CrosspostSource {
  pk: number;
  resource: number;
  resourceName: string;
  externalId: string;
  publishedAt: string | null;
  permalinkUrl: string | null;
  reach: number | null;
  impressions: number | null;
}

// A Facebook story shared from this Instagram story.
export interface CrosspostCopy {
  pk: number;
  resource: number;
  resourceName: string;
  externalId: string;
  publishedAt: string | null;
}

export interface Post {
  pk: number;
  resource: number;
  channel: SocialMediaResourceChannel;
  externalId: string;
  publishedAt: string | null;
  text: string;
  assets: PostAsset[];
  postType: string;
  destinationUrl: string;
  permalinkUrl: string | null;
  openGraphData: OpenGraphData[];
  isSponsored: boolean;
  // Instagram media another account published with this one as collaborator.
  isCollab: boolean;
  // Null on a collab post for a count Meta doesn't serve or the publisher hides.
  impressions: number | null;
  clicks: number;
  comments: number | null;
  reactions: number | null;
  shares: number | null;
  clickthroughRate: number | null;
  engagement: number | null;
  engagementRate: number | null;
  reach: number | null;
  saves: number | null;
  linkClicks: number | null;
  videoWatchTime: number | null;
  storyInteractions: number | null;
  storyReplies: number | null;
  storyFollows: number | null;
  storyProfileVisits: number | null;
  storyTapsForward: number | null;
  storyTapsBack: number | null;
  storyExits: number | null;
  storySwipeForward: number | null;
  pageViewsPerSession: number | null;
  conversions: number | null;
  bounceRate: number | null;
  [calculatedMetric: CalculatedMetricKey]: number | null;
  organicImpressions: number | null;
  organicClicks: number;
  organicComments: number | null;
  organicReactions: number | null;
  organicShares: number | null;
  organicClickthroughRate: number | null;
  organicEngagement: number | null;
  organicEngagementRate: number | null;
  sponsoredImpressions: number | null;
  sponsoredClicks: number;
  sponsoredComments: number;
  sponsoredReactions: number;
  sponsoredShares: number;
  sponsoredClickthroughRate: number | null;
  sponsoredEngagement: number;
  sponsoredEngagementRate: number | null;
  tags: number[];
  // Whether the user-facing "Reload preview" action should be shown for this
  // post. True when at least one asset still points at a Meta CDN (likely to
  // expire) and the per-post cooldown has passed. Backend computes this — see
  // Post.can_refetch_manually.
  canRefetchAssets: boolean;
  crosspostSource?: CrosspostSource | null;
  crosspostCopies?: CrosspostCopy[];
  extra?: {
    title?: string;
    collabUsername?: string;
  };
}

export interface PostsResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: Post[];
}

export const POST_FORMAT_LABEL = "Format";
export const POST_FORMAT_OPTION_LABELS: Readonly<
  Record<PostFormatView, string>
> = {
  all: "All formats",
  posts: "Photos & other",
  video: "Video & reels",
  stories: "Stories",
};
export const COLLAB_LABEL = "Collabs";
export const COLLAB_OPTION_LABELS: Readonly<Record<CollabView, string>> = {
  include: "Include collabs",
  exclude: "Exclude collabs",
  only: "Only collabs",
};

export function postFormatViewToParam(
  view: PostFormatView | undefined,
): string | undefined {
  return view === undefined || view === "all" ? undefined : view;
}

export function collabViewToIsCollab(
  view: CollabView | undefined,
): boolean | undefined {
  if (view === "only") return true;
  if (view === "exclude") return false;
  return undefined;
}

type PostsParams = {
  filterCriteria?: MetricFilterSet[];
  page?: number | undefined;
  // null asks for every post; undefined gets the backend's default page.
  pageSize: number | null | undefined;
  ordering: AscendingOrDescending<SocialMediaPostSortKey> | undefined;
  pks: number[] | undefined;
  is_sponsored?: boolean | undefined;
  promoted_only?: boolean | undefined;
  include_stories?: boolean | undefined;
  post_formats?: string | undefined;
  is_collab?: boolean | undefined;
} & SelectionTemporalDataFilter &
  ResourcesDatafilter &
  TagsDataFilter;

function listPosts({
  queryKey: [, params],
  ...context
}:
  | QueryFunctionContext<PostsParams, number | undefined>
  | QueryFunctionContext<PostsParams>): Promise<PostsResponse> {
  const filterCriteria = filterCriteriaFormatter(params.filterCriteria);
  const {
    is_sponsored,
    promoted_only,
    include_stories,
    post_formats,
    is_collab,
    ...restParams
  } = params;
  return api
    .get("/social_media/posts/", {
      params: {
        ...restParams,
        ...filterCriteria,
        page: "pageParam" in context ? context.pageParam : params.page,
        pageSize: params.pageSize === null ? "null" : params.pageSize,
        ...(is_sponsored !== undefined && { is_sponsored }),
        ...(promoted_only !== undefined && { promoted_only }),
        // Only sent when the widget's toggle opts out (false); absent → the
        // backend includes stories (default), keeping the Posts tab unchanged.
        ...(include_stories !== undefined && { include_stories }),
        ...(post_formats !== undefined && { post_formats }),
        ...(is_collab !== undefined && { is_collab }),
      },
    })
    .then((response) => response.data);
}

export function usePostsQuery({
  filterCriteria,
  selectedResourceIds,
  includedTags,
  excludedTags,
  includedTagsLogic,
  temporalFilter,
  page,
  pageSize,
  ordering,
  pks,
  search,
  isSponsored,
  promotedOnly,
  includeStories,
  postFormats,
  isCollab,
  useGlobalFilters = true,
  enabled,
}: {
  filterCriteria?: Ref<MetricFilterSet[]>;
  selectedResourceIds?: Ref<number[]>;
  includedTags?: Ref<number[]>;
  excludedTags?: Ref<number[]>;
  includedTagsLogic?: Ref<"AND" | "OR">;
  temporalFilter?: Ref<DateRangeSelection>;
  page?: Ref<number>;
  pageSize?: Ref<number | null>;
  ordering?: Ref<AscendingOrDescending<SocialMediaPostSortKey>>;
  pks?: Ref<number[]>;
  search?: Ref<string>;
  isSponsored?: Ref<boolean | undefined>;
  promotedOnly?: Ref<boolean | undefined>;
  includeStories?: Ref<boolean | undefined>;
  postFormats?: Ref<string | undefined>;
  isCollab?: Ref<boolean | undefined>;
  useGlobalFilters?: boolean;
  enabled?: Ref<boolean>;
} = {}) {
  const queryKey = [
    "posts",
    {
      ...useCalculatedResources(selectedResourceIds, useGlobalFilters),
      ...useDetachedTagFilter(
        useGlobalFilters,
        includedTags,
        excludedTags,
        includedTagsLogic,
      ),
      ...useDetachedSelectionTemporalFilter(temporalFilter, useGlobalFilters),
      ...usePublicDashboardWidgetQueryFilters(),
      filterCriteria,
      page,
      pageSize,
      ordering,
      pks,
      search,
      is_sponsored: isSponsored,
      promoted_only: promotedOnly,
      include_stories: includeStories,
      post_formats: postFormats,
      is_collab: isCollab,
    },
  ] as const;
  return reactive(useQuery({ queryKey, queryFn: listPosts, enabled }));
}

export function usePostsInfiniteQuery({
  filterCriteria,
  selectedResourceIds,
  includedTags,
  excludedTags,
  includedTagsLogic,
  temporalFilter,
  pageSize,
  ordering,
  pks,
  search,
  isSponsored,
  promotedOnly,
  includeStories,
  postFormats,
  isCollab,
  useGlobalFilters,
  enabled,
}: {
  filterCriteria?: Ref<MetricFilterSet[]>;
  selectedResourceIds?: Ref<number[]>;
  includedTags?: Ref<number[]>;
  excludedTags?: Ref<number[]>;
  includedTagsLogic?: Ref<"AND" | "OR">;
  temporalFilter?: Ref<DateRangeSelection>;
  pageSize: MaybeRef<number>;
  ordering: Ref<AscendingOrDescending<SocialMediaPostSortKey>>;
  pks?: Ref<number[]>;
  search?: Ref<string>;
  isSponsored?: Ref<boolean | undefined>;
  promotedOnly?: Ref<boolean | undefined>;
  includeStories?: Ref<boolean | undefined>;
  postFormats?: Ref<string | undefined>;
  isCollab?: Ref<boolean | undefined>;
  useGlobalFilters?: boolean;
  enabled?: Ref<boolean>;
}) {
  const queryKey = [
    "posts",
    {
      ...useCalculatedResources(selectedResourceIds, useGlobalFilters),
      ...useDetachedTagFilter(
        useGlobalFilters,
        includedTags,
        excludedTags,
        includedTagsLogic,
      ),
      ...useDetachedSelectionTemporalFilter(temporalFilter, useGlobalFilters),
      ...usePublicDashboardWidgetQueryFilters(),
      filterCriteria,
      pageSize,
      ordering,
      pks,
      search,
      is_sponsored: isSponsored,
      promoted_only: promotedOnly,
      include_stories: includeStories,
      post_formats: postFormats,
      is_collab: isCollab,
    },
  ] as const;
  return reactive(
    useInfiniteQuery({
      queryKey,
      queryFn: listPosts,
      initialPageParam: undefined,
      getNextPageParam: (lastPage, pages) =>
        lastPage.next ? pages.length + 1 : undefined,
      enabled,
    }),
  );
}

function getPost({
  queryKey: [, { pk }],
}: QueryFunctionContext<{ pk: number }>): Promise<Post> {
  return api
    .get(`/social_media/posts/${pk}/`)
    .then((response) => response.data);
}

export function useSinglePostQuery({ pk }: { pk: Ref<number> }) {
  const queryClient = useQueryClient();
  const queryKey = ["post", { pk }] as const;
  return reactive(
    useQuery({
      queryKey,
      queryFn: getPost,
      initialData: () => {
        return queryClient
          .getQueriesData<PostsResponse | undefined>({ queryKey: ["posts"] })
          .flatMap(([_, postsResponse]) => postsResponse?.results ?? [])
          .find((post) => post.pk === pk.value);
      },
    }),
  );
}

function reloadPostPreview(pk: number): Promise<Post> {
  return api
    .post(`/social_media/posts/${pk}/reload-preview/`)
    .then((response) => response.data);
}

/**
 * Re-fetches a post from its source platform and re-caches its assets to our
 * CDN. Used when a Meta CDN URL has expired and the cached preview shows
 * "asset not available". Sync — the response carries the updated post.
 */
export function useReloadPostPreviewMutation() {
  const queryClient = useQueryClient();
  return reactive(
    useMutation({
      mutationFn: reloadPostPreview,
      onSuccess: (post: Post) => {
        // Key shape must match useSinglePostQuery — see line ~789.
        queryClient.setQueryData<Post>(["post", { pk: post.pk }], post);

        // Patch any list cache that contains this post. Both the paginated
        // (PostsResponse) and infinite (InfiniteData<PostsResponse>) shapes
        // are keyed under ["posts", ...]; the shape is discriminated by
        // `pages` (only InfiniteData has it) vs `results` (only PostsResponse
        // has it at the top level — pages each have their own `results`).
        function swap(p: Post): Post {
          return p.pk === post.pk ? post : p;
        }
        queryClient
          .getQueriesData<PostsResponse | InfiniteData<PostsResponse>>({
            queryKey: ["posts"],
          })
          .forEach(([key, response]) => {
            if (!response) return;
            if ("pages" in response) {
              // Find the page containing the post first; only rebuild that
              // page (and skip the cache write entirely if no page contains
              // it) so we don't trigger a re-render of every consumer for an
              // unrelated cache.
              const pageIdx = response.pages.findIndex((page) =>
                page.results.some((p) => p.pk === post.pk),
              );
              if (pageIdx === -1) return;
              const next: InfiniteData<PostsResponse> = {
                ...response,
                pages: response.pages.map((page, i) =>
                  i === pageIdx
                    ? { ...page, results: page.results.map(swap) }
                    : page,
                ),
              };
              queryClient.setQueryData(key, next);
            } else if ("results" in response) {
              const idx = response.results.findIndex((p) => p.pk === post.pk);
              if (idx === -1) return;
              const next: PostsResponse = {
                ...response,
                results: response.results.map(swap),
              };
              queryClient.setQueryData(key, next);
            }
          });
      },
    }),
  );
}

export function determinePostType(
  inputPost:
    | Post
    | CompetitorPost
    | ComputedRef<Post | CompetitorPost>
    | undefined,
): ComputedRef<PostType> {
  return computed(() => {
    if (!inputPost) {
      return PostType.UNKNOWN;
    }
    const post = unref(inputPost);
    if (post && post.postType) {
      const media = post.postType.toLowerCase();

      if (media in ImagePostTypes) {
        return PostType.IMAGE;
      } else if (media in VideoPostTypes) {
        return PostType.VIDEO;
      } else if (media in DocumentPostTypes) {
        return PostType.DOCUMENT;
      } else if (media in LinkPostTypes) {
        return PostType.LINK;
      } else if (media in SharePostTypes) {
        return PostType.SHARE;
      } else if (media in CollectionPostTypes) {
        return PostType.COLLECTION;
      } else if (media in VideoThumbnailPostTypes) {
        return PostType.VIDEO_THUMBNAIL;
      } else if (media in PageUpdatePostTypes) {
        return PostType.PAGE_UPDATE;
      } else if (media === "story") {
        // "story" is a product type, not a media type — classify by the
        // story's own (first) asset. Stories are single-media, but guard the
        // rare multi-asset case by using the first. 0 assets falls through to
        // UNKNOWN → the graceful missing-asset placeholder (correct: no media).
        if (post.assets.length >= 1) {
          const assetType = post.assets[0].mediaType.toLowerCase();
          if (assetType in PostAssetVideoTypes || assetType === "video") {
            return PostType.VIDEO;
          }
          // Default a story with an asset to IMAGE (covers "photo" + anything
          // non-video) so it renders rather than falling to the placeholder.
          return PostType.IMAGE;
        }
      } else if (media in FeedPostTypes) {
        if (post.assets.length > 1) {
          return PostType.COLLECTION;
        } else if (post.assets.length === 1) {
          if (post.assets[0].mediaType.toLowerCase() in PostAssetImageTypes) {
            return PostType.IMAGE;
          } else if (
            post.assets[0].mediaType.toLowerCase() in PostAssetVideoTypes
          ) {
            return PostType.VIDEO;
          }
        }
      } else if (post.openGraphData[0]) {
        return PostType.LINK;
      }
    }
    return PostType.UNKNOWN;
  });
}

export function getSocialMediaProfileImageUrl(
  inputPost: Post | CompetitorPost | ComputedRef<Post | CompetitorPost>,
): ComputedRef<string | undefined> {
  const resources = useResourcesQuery();
  const competitors = useCompetitorsQuery({
    enabled: computed(() => isCompetitorPost(unref(inputPost))),
  });
  return computed(() => {
    const post = unref(inputPost);
    if (isCompetitorPost(post)) {
      const competitor = competitorById(post.competitor, competitors);
      if (post.channel === ResourceChannel.FACEBOOK_ORGANIC) {
        return competitor?.facebookProfileImageUrl ?? undefined;
      } else if (post.channel === ResourceChannel.INSTAGRAM_ORGANIC) {
        return competitor?.instagramProfileImageUrl ?? undefined;
      } else {
        return "";
      }
    } else {
      const resource = resources.data?.find(
        (resource) => resource.pk === (post as Post).resource,
      ) as FacebookOrganicResource | InstagramOrganicResource | undefined;
      return resource?.metadata.profileImageUrl ?? undefined;
    }
  });
}

export function getSocialMediaProfileObject(
  inputPost: Post | CompetitorPost | ComputedRef<Post | CompetitorPost>,
): ComputedRef<Resource | Competitor | undefined> {
  const resources = useResourcesQuery();
  const competitors = useCompetitorsQuery({
    enabled: computed(() => isCompetitorPost(unref(inputPost))),
  });
  return computed(() => {
    const post = unref(inputPost);
    if (isCompetitorPost(post)) {
      return competitorById(post.competitor, competitors);
    }
    return resources.data?.find(
      (resource) => resource.pk === (post as Post).resource,
    );
  });
}

export type PostFrequency = HeatmapWeekdayData;

export function listPostFrequency({
  queryKey: [, params],
}: QueryFunctionContext<
  SelectionTemporalDataFilter & TagsDataFilter & ResourcesDatafilter
>): Promise<PostFrequency> {
  return api
    .get(`/social_media/post_frequency/`, {
      params: { ...params, timeZone: DateTime.local().zoneName },
    })
    .then((response) => response.data);
}

export function usePostFrequencyQuery({
  includedTags,
  excludedTags,
  includedTagsLogic,
  selectedResourceIds,
  temporalFilter,
  useGlobalFilters = true,
  enabled,
}: {
  includedTags?: Ref<number[]>;
  excludedTags?: Ref<number[]>;
  includedTagsLogic?: Ref<"AND" | "OR">;
  selectedResourceIds?: Ref<number[]>;
  temporalFilter?: Ref<DateRangeSelection>;
  useGlobalFilters?: boolean;
  enabled?: Ref<boolean>;
} = {}) {
  const queryKey = [
    "competitorPostFrequency",
    {
      ...useCalculatedResources(selectedResourceIds, useGlobalFilters),
      ...useDetachedTemporalFilter(temporalFilter, useGlobalFilters),
      ...useDetachedTagFilter(
        useGlobalFilters,
        includedTags,
        excludedTags,
        includedTagsLogic,
      ),
      ...useDetachedSelectionTemporalFilter(temporalFilter, useGlobalFilters),
      ...usePublicDashboardWidgetQueryFilters(),
    },
  ] as const;
  return reactive(
    useQuery({
      queryKey,
      queryFn: listPostFrequency,
      placeholderData: keepPreviousData,
      enabled,
    }),
  );
}

/**
 * Used Claude to make this load as a global composable instead of loading per post.
 **/

type EmbedState = "unloaded" | "loading" | "loaded" | "error";
const globalEmbedState: Ref<EmbedState> = ref("unloaded");
const scriptLoadPromise: Ref<Promise<void> | null> = ref(null);

/**
 * Composable for managing TikTok embed script loading
 * Ensures the script is only loaded once, regardless of how many posts need it
 */
export function useTikTokEmbed() {
  /**
   * Load the TikTok embed script (only once globally)
   */
  function loadTikTokScript(): Promise<void> {
    // If already loaded, return immediately
    if (globalEmbedState.value === "loaded") {
      return Promise.resolve();
    }

    // If currently loading, return the existing promise
    if (scriptLoadPromise.value) {
      return scriptLoadPromise.value;
    }

    // Start loading
    globalEmbedState.value = "loading";

    scriptLoadPromise.value = new Promise((resolve, reject) => {
      // Check if script already exists in DOM
      const existingScript = document.querySelector(
        'script[src="https://www.tiktok.com/embed.js"]',
      );

      if (existingScript) {
        globalEmbedState.value = "loaded";
        resolve();
        return;
      }

      const script = document.createElement("script");
      script.src = "https://www.tiktok.com/embed.js";
      script.async = true;

      script.onload = () => {
        globalEmbedState.value = "loaded";
        resolve();
      };

      script.onerror = () => {
        globalEmbedState.value = "error";
        scriptLoadPromise.value = null; // Allow retry
        reject(new Error("Failed to load TikTok embed script"));
      };

      document.head.appendChild(script);
    });

    return scriptLoadPromise.value;
  }

  /**
   * Fetch TikTok embed data for a specific post
   */
  async function fetchTikTokEmbed(
    username: string,
    videoId: string,
  ): Promise<{
    html: string;
    thumbnail_url?: string;
    width?: string | number;
    height?: string | number;
  }> {
    const url = `https://www.tiktok.com/oembed?url=https://www.tiktok.com/@${username}/video/${videoId}`;

    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`TikTok API error: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error("Failed to fetch TikTok embed:", error);
      throw error;
    }
  }

  return {
    embedState: globalEmbedState,
    loadTikTokScript,
    fetchTikTokEmbed,
  };
}
/**
 * End of Claude
 **/
